import { Temporal } from '@js-temporal/polyfill'
import type { GatDateEditProps } from '../gat-date-edit.vue'
import { computed, onMounted, ref } from 'vue'
import { useDateInputModel } from './useModelValue'

export function useTimezone(
  props: GatDateEditProps,
  updateModel: ReturnType<typeof useDateInputModel>['updateModel']
) {
  /** The system timezone of the browser. */
  const systemTimezone = computed(() => Intl.DateTimeFormat().resolvedOptions().timeZone)

  /** The selected timezone. */
  const selectedTimeZone = ref(props.timeZone ?? systemTimezone.value)

  onMounted(() => {
    selectedTimeZone.value = props.timeZone || systemTimezone.value
  })

  /** Set the selected timezone. */
  function setSelectedTimeZone(timezone: string) {
    selectedTimeZone.value = timezone
    updateModel(props.modelValue, {
      timeZone: timezone
    })
  }

  const availableTimezones = computed(() => {
    return [systemTimezone.value, props.timeZone].filter((i) => i !== undefined)
  })

  const localDateTime = computed(() => {
    return props.modelValue
      ? Temporal.Instant.from(props.modelValue).toLocaleString(
          Intl.DateTimeFormat().resolvedOptions().locale,
          {
            dateStyle: 'medium',
            timeStyle: 'short',
            hour12: false
          }
        )
      : ''
  })

  const invertedTimezone = computed(() => {
    return selectedTimeZone.value === systemTimezone.value
      ? (props.timeZone ?? systemTimezone.value)
      : systemTimezone.value
  })

  const timezoneDateTime = computed(() => {
    return props.modelValue
      ? Temporal.Instant.from(props.modelValue)
          .toZonedDateTimeISO(invertedTimezone.value)
          .toLocaleString(Intl.DateTimeFormat().resolvedOptions().locale, {
            dateStyle: 'medium',
            timeStyle: 'short',
            hour12: false
          })
      : ''
  })

  return {
    systemTimezone,
    localDateTime,
    timezoneDateTime,
    invertedTimezone,
    selectedTimeZone,
    availableTimezones,
    setSelectedTimeZone
  }
}
